// src/Landing.js — Marketing splash, rebranded 2026-09-04 to the two-tone
// Jckrbbt system: charcoal (#1E1E1E) hero with mint (#7FF2B8) type, a white
// showcase where a phone fades through the app's screens as you scroll, a
// mint closing band, and Manrope everywhere (see Landing.css). No WebGL.
//
// The hero's visual is a real "Found by Jckrbbt" share card exported from the
// app (public/found-by.png), so the first thing a visitor sees is the receipt
// the product is built around, not a stock-photo phone.
import React, { useRef, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './Landing.css';

const APP_STORE_URL = 'https://apps.apple.com/app/id6759627076';

// Brand tokens (mirror constants/brand.js in the app)
const MINT = '#7FF2B8';
const CHARCOAL = '#1E1E1E';
const INK = '#131314';
const ON_MINT = '#0A3D16';
const BODY_MUTED = '#5F5F63';
const DIM = '#9A9AA0';

// Screenshots are iPhone 17 Pro captures scaled to 644×1400. The phone frame
// uses this exact aspect so the full screen shows uncropped.
const SHOT_ASPECT = '644 / 1400';

const SCREENS = [
  { img: '/shots/01-home.png',      kicker: "JACQ'S READ",  title: 'Your morning,\nread for you',          body: 'Jacq, the in-app analyst, reads the tape every morning and hands you the few insights that matter, grounded in real data.' },
  { img: '/shots/02-scanner.png',   kicker: 'SCANNER',      title: 'Find setups\nbefore the crowd',        body: 'Scan 8,000+ stocks for unusual options flow, early signals, and news catalysts, ranked by conviction, not noise.' },
  { img: '/shots/03-detail.png',    kicker: 'STOCK DETAIL', title: 'Every signal,\none clear read',        body: 'Live charts, fundamentals, options activity, and the scanner’s own track record on the name, all in a single scroll.' },
  { img: '/shots/04-scorecard.png', kicker: 'SCORECARD',    title: 'Calls you\ncan check',                 body: 'Every pick is tracked from the moment it is flagged: win rate, average move, and alpha against the S&P 500.' },
  { img: '/shots/05-foundby.png',   kicker: 'RECEIPTS',     title: 'When a call runs,\nyou get the receipt', body: 'A shareable card with the move since Jckrbbt found it, and a chart of exactly that window. Built for your feed.' },
  { img: '/shots/06-intel.png',     kicker: 'MARKET INTEL', title: 'See what’s\nmoving right now',      body: 'Trending names, top movers, and unusual volume across the market, refreshed through the session.' },
  { img: '/shots/07-portfolio.png', kicker: 'PORTFOLIO',    title: 'Track it against\nthe market',          body: 'Connect your brokerages for combined performance, income, and a live benchmark against the S&P 500.' },
];
const N = SCREENS.length;

function AppStoreBadge({ small, onMint }) {
  const bg = onMint ? INK : (small ? INK : MINT);
  const fg = onMint ? '#fff' : (small ? '#fff' : ON_MINT);
  return (
    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="lp-badge"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: bg, color: fg,
        padding: small ? '9px 15px' : '15px 24px', borderRadius: small ? 11 : 14, textDecoration: 'none' }}>
      <svg width={small ? 19 : 24} height={small ? 19 : 24} viewBox="0 0 24 24" fill={fg}><path d="M17.05 12.04c-.03-2.85 2.33-4.22 2.44-4.28-1.33-1.95-3.4-2.22-4.13-2.25-1.76-.18-3.43 1.04-4.32 1.04-.89 0-2.26-1.01-3.72-.99-1.91.03-3.68 1.11-4.66 2.82-1.99 3.45-.51 8.55 1.43 11.35.95 1.37 2.08 2.9 3.56 2.85 1.43-.06 1.97-.92 3.7-.92 1.72 0 2.21.92 3.72.89 1.54-.03 2.51-1.39 3.45-2.77 1.09-1.59 1.54-3.13 1.56-3.21-.03-.01-2.99-1.15-3.02-4.55zM14.28 3.69c.79-.96 1.32-2.29 1.18-3.61-1.14.05-2.52.76-3.33 1.71-.73.85-1.37 2.2-1.2 3.5 1.27.1 2.57-.65 3.35-1.6z"/></svg>
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'left' }}>
        <span className="w500" style={{ fontSize: small ? 9 : 11, opacity: 0.75 }}>Download on the</span>
        <span className="w700" style={{ fontSize: small ? 14 : 18 }}>App Store</span>
      </span>
    </a>
  );
}

function Wordmark({ light }) {
  return (
    <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }} aria-label="Jckrbbt home">
      <img src="/rabbit.png" alt="" draggable={false}
        style={{ width: 18, height: 26, filter: light ? 'invert(1)' : 'none' }} />
      <span className="w600" style={{ fontSize: 22, letterSpacing: 1.2, color: light ? "#fff" : INK }}>Jckrbbt</span>
    </a>
  );
}

// The whole mockup fades into the page background and back between screens.
function Phone({ src, i, progress }) {
  const step = 1 / Math.max(N - 1, 1);
  const at = i * step;
  const out = step * 0.42;
  const hold = step * 0.20;
  const opacity = useTransform(progress, [at - out, at - hold, at + hold, at + out], [0, 1, 1, 0], { clamp: true });
  return (
    <motion.div style={{ position: 'absolute', inset: 0, borderRadius: 44, overflow: 'hidden', background: '#fff',
      border: '1px solid rgba(19,19,20,0.12)', opacity }}>
      <img src={src} alt="" draggable={false} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
    </motion.div>
  );
}

// Copy slides up as it fades, distinct from the phone's dissolve.
function Copy({ s, i, progress }) {
  const step = 1 / Math.max(N - 1, 1);
  const at = i * step;
  const out = step * 0.46;
  const hold = step * 0.16;
  const opacity = useTransform(progress, [at - out, at - hold, at + hold, at + out], [0, 1, 1, 0], { clamp: true });
  const y = useTransform(progress, [at - out, at - hold, at + hold, at + out], [38, 0, 0, -38], { clamp: true });
  return (
    <motion.div style={{ position: 'absolute', top: 0, left: 0, right: 0, opacity, y }}>
      <div className="lp-term w600">{s.kicker.toLowerCase()}<span className="lp-term-cursor">_</span><span className="lp-term-index">{String(i + 1).padStart(2, '0')} / {String(N).padStart(2, '0')}</span></div>
      <h2 className="w700" style={{ fontSize: 'clamp(34px, 6.4vw, 54px)', lineHeight: 1.04, letterSpacing: '-1px', margin: '18px 0 16px', whiteSpace: 'pre-line', color: INK }}>{s.title}</h2>
      <p style={{ fontSize: 'clamp(15px, 2.2vw, 18px)', lineHeight: 1.6, color: BODY_MUTED, margin: 0, maxWidth: 440 }}>{s.body}</p>
    </motion.div>
  );
}

export default function Landing() {
  const showcaseRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: showcaseRef, offset: ['start start', 'end end'] });
  const GUTTER = 'clamp(24px, 7vw, 80px)';

  useEffect(() => {
    document.documentElement.classList.add('lp-no-scrollbar');
    return () => document.documentElement.classList.remove('lp-no-scrollbar');
  }, []);

  return (
    <div className="landing-root" style={{ background: '#fff', color: INK, minHeight: '100vh' }}>

      {/* HERO — charcoal field, mint campaign line, the receipt as the visual */}
      <section style={{ background: CHARCOAL, color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div className="lp-grain" aria-hidden="true" />
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `26px ${GUTTER} 0` }}>
          <Wordmark light />
          <AppStoreBadge small onMint />
        </header>
        <div className="lp-hero" style={{ padding: `clamp(48px, 8vw, 110px) ${GUTTER} clamp(56px, 8vw, 120px)` }}>
          <div className="lp-hero-copy">
            <h1 className="w700" style={{ fontSize: 'clamp(46px, 8.6vw, 96px)', lineHeight: 0.96, letterSpacing: '-2.5px', margin: '0 0 30px', color: '#fff' }}>
              Don't chase<br />the market.<br /><span style={{ color: MINT }}>Outrun it<span className="lp-cursor">_</span></span>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2.2vw, 20px)', lineHeight: 1.6, color: 'rgba(255,255,255,0.72)', margin: '0 0 34px', maxWidth: 520 }}>
              Jckrbbt scans 8,000+ stocks for unusual options flow, early signals, and news catalysts, ranks them by conviction, and shows you its receipts.
            </p>
            <AppStoreBadge />
          </div>
          <div className="lp-hero-art">
            <img src="/found-by.png" alt="A Found by Jckrbbt card: NVDA up 12.45% since Jckrbbt found it, 4 days ago" draggable={false}
              style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 28, border: '1px solid rgba(255,255,255,0.14)' }} />
          </div>
        </div>
      </section>

      {/* SHOWCASE — phone fades through the app's screens, scroll-driven */}
      <section ref={showcaseRef} style={{ position: 'relative', height: `${N * 92}vh`, background: '#fff' }}>
        <div className="lp-stage" style={{ position: 'sticky', top: 0, height: '100vh', display: 'flex',
          padding: `clamp(32px, 5vh, 60px) ${GUTTER}`, overflow: 'hidden' }}>

          {/* Right-edge scroll progress: charcoal fill on a mint track */}
          <div style={{ position: 'absolute', right: 'clamp(10px, 2.6vw, 22px)', top: '50%', transform: 'translateY(-50%)',
            height: 'min(48vh, 440px)', width: 4, borderRadius: 3, background: MINT, overflow: 'hidden' }}>
            <motion.div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '100%', borderRadius: 3,
              background: CHARCOAL, transformOrigin: 'top', scaleY: scrollYProgress }} />
          </div>

          <div className="lp-phonewrap">
            {/* The slab offsets 14px down-right, so the frame carries matching margins: the
                 flex centering then centres the phone+slab composite, not just the phone. */}
              <div style={{ position: 'relative', maxWidth: 'calc(100% - 14px)', aspectRatio: SHOT_ASPECT, marginRight: 14, marginBottom: 14,
              height: 'min(calc(100% - 14px), 880px, calc((100vw - 2 * clamp(24px, 7vw, 80px) - 14px) * 2.1739))' }}>
              {/* Flat offset slab, the app's two-tone card grammar, in place of a drop shadow. */}
              <div aria-hidden="true" style={{ position: 'absolute', inset: 0, transform: 'translate(14px, 14px)', borderRadius: 44, background: MINT }} />
              {SCREENS.map((s, i) => <Phone key={s.img} src={s.img} i={i} progress={scrollYProgress} />)}
            </div>
          </div>

          <div className="lp-copy">
            {SCREENS.map((s, i) => <Copy key={s.img} s={s} i={i} progress={scrollYProgress} />)}
          </div>
        </div>
      </section>

      {/* CLOSING — mint band */}
      <section style={{ background: MINT, color: ON_MINT, padding: `clamp(56px, 9vw, 120px) ${GUTTER}` }}>
        <div className="lp-close">
          <img src="/rabbit.png" alt="" draggable={false} style={{ display: 'block', width: 40, height: 58 }} />
          <div>
            <h2 className="w700" style={{ fontSize: 'clamp(38px, 7.5vw, 88px)', lineHeight: 0.98, letterSpacing: '-2.5px', margin: '0 0 34px', color: ON_MINT }}>
              Made for the ones<br />who show up early<span className="lp-cursor">_</span>
            </h2>
            <AppStoreBadge onMint />
          </div>
        </div>
      </section>

      <footer style={{ padding: `26px ${GUTTER}`, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 22, fontSize: 14, color: DIM, background: '#fff', borderTop: '1px solid rgba(19,19,20,0.10)' }}>
        <span>jckrbbt<span style={{ color: '#3DBE6E' }}>_</span> © {new Date().getFullYear()}</span>
        <span style={{ display: 'flex', gap: 22 }}>
          <a href="/guides/" style={{ color: DIM, textDecoration: 'none' }}>Guides</a>
          <a href="/privacy" style={{ color: DIM, textDecoration: 'none' }}>Privacy</a>
          <a href="/terms" style={{ color: DIM, textDecoration: 'none' }}>Terms</a>
        </span>
      </footer>
    </div>
  );
}
